import { CABIN_CLASS } from "../../enums/CABIN_CLASS";
import { ROUTE_TYPE } from "../../enums/ROUTE_TYPE";
import { Airline } from "./Airline";
import {
  BaggageInfo,
  ExtendedPrice,
  PaxTypeFare,
  PaxTypePriceBreakdown,
  Price,
} from "./BookingResponse";
import { JourneyLeg } from "./JourneyLeg";
import { Leg } from "./Leg";
import { Segment } from "./Segment";
import { Place } from "./Place";
import { IAdditionalSurcharges } from "./FlightBookingForm";

export enum DiscountType {
  PERCENTAGE = "PERCENTAGE",
  FIXED = "FIXED",
}

export interface DealDiscount {
  discount_code: string;
  discount_offer: string;
  discount_type: string;
  discount_amount: number;
  discount_on_gross_fare: boolean;
  discount_currency: string | null;
}

export type TravelerCount = {
  adult_count: number;
  child_count: number;
  infant_count: number;
};

export type FlightsSearchBaseQuery = {
  route_type: ROUTE_TYPE;
  departure_date: Date;
  return_date: Date;
  cabin_class: CABIN_CLASS;
  traveler_count: TravelerCount;
  non_stop_flight: boolean;
  next_leg: number;
  current_leg: number;
  total_legs: number;
};

export type SelectedFlightLeg = FlightOption & {
  origin: Place;
  destination: Place;
  selectedFareOption: FareOption;
};

export type FlightPolicy = {
  policy_rules: Array<{
    rule: string;
    value: number;
  }>;
  notes: Array<string>;
};

export type FlightRouteStateParams = FlightsSearchBaseQuery & {
  legs: Leg[];
};

export type FlightRuleParams = {
  pre_booking_token: string;
};

export interface PaxTypeFareOption {
  pre_booking_token: string;
  price: PaxTypeFareBreakdown | null;
  status: "EXPIRED" | string | null;
}

export interface PaxTypeFareBreakdown extends PaxTypeFare {
  pax_type_fare_breakdown: PaxTypePriceBreakdown;
}

export interface PaxWiseFareBreakdownResponse {
  fare_options: PaxTypeFareOption[];
  equivalent_currency: string | null;
}

export type FlightPreBookingTokenType = {
  pre_booking_tokens: string[];
  financial_profile_id: string;
  currency_code?: string;
};

export enum BUDGET_DURATION_BASIS {
  FLYING_TIME = "FLYING_TIME",
}

export type BudgetPolicyDurationLimit = {
  min_minutes: number | null;
  max_minutes: number | null;
  max_fare: ExtendedPrice | null;
  max_fare_per_traveler: ExtendedPrice | null;
  action: VIOLATION_ACTION;
};

export type BudgetPolicy = {
  duration_limits: BudgetPolicyDurationLimit[];
  duration_basis: BUDGET_DURATION_BASIS;
};

export type FlightResponse = {
  is_international: boolean;
  route_type: ROUTE_TYPE;
  poll_id: string | null;
  keep_polling: boolean;
  journey_legs: Array<JourneyLeg>;
  budget_policy?: BudgetPolicy | null;
  budget_policy_applicable?: boolean;
  full_result: boolean;
};

export type FlightDetailsType = {
  passengers: any[];
  flight_legs: FlightLeg[];
  deal_discount?: ExtendedPrice | null;
  agent_total_amount?: ExtendedPrice | null;
  price_difference?: ExtendedPrice | null;
  total_amount_after_pricing: ExtendedPrice;
  ag_gross_fare: ExtendedPrice;
  price: Price & {
    currency: string;
    pax_type_price_breakdown: PaxTypePriceBreakdown;
  };
  flight_provider: string;
  flight_sub_provider: string;
  provider_booking_token: string;
  provider_confirmation_token: null | string;
  supplier_pnr: null | string;
  airline_pnr: null | string;
  route_type: ROUTE_TYPE;
  adult_count: number;
  child_count: number;
  infant_count: number;
  cabin_class: CABIN_CLASS;
  is_international: boolean;
  sector: string;
  pnr_expiry_status: string;
  raw_pnr_expiry: string[] | null;

  display_agent_pricing?: boolean;
  equivalent_agent_total_amount?: null | number;
  equivalent_total_amount_after_pricing?: null | number;
  discount_data: DealDiscount;
  is_addons_skipped: boolean;
  surcharges_xi: IAdditionalSurcharges | null;
};

export interface FlightDetails {
  passengers: any[];
  flight_legs: FlightLeg[];
  price: Price & {
    currency: string;
    pax_type_price_breakdown: PaxTypePriceBreakdown;
  };
  flight_provider: string;
  flight_sub_provider: string;
  provider_booking_token: string;
  provider_confirmation_token: null | string;
  supplier_pnr: null | string;
  airline_pnr: null | string;
  route_type: ROUTE_TYPE;
  adult_count: number;
  child_count: number;
  infant_count: number;
  cabin_class: CABIN_CLASS;
  is_international: boolean;
  sector: string;
  pnr_expiry_status: string;
  equivalent_total_amount_after_pricing?: null | number;
}

export type FlightLeg = {
  segment: Segment[];
  origin: Place;
  destination: Place;
  departure_datetime: Date;
  arrival_datetime: Date;
  is_refundable: boolean;
  has_meal: boolean;
  baggage_info: BaggageInfo;
  leg_duration: number;
  public_id: string;
};

export interface AdditionalFlightDetails {
  hasMeal: boolean;
  baggageInfo: string;
}

export type MulticityLeg = {
  departure_date: Date;
  origin: string;
  destination: string;
};

export type MultiCityLegStateType = {
  origin: Place | null;
  destination: Place | null;
  departure_date: Date;
};

export type FetchFlightsPayload = {
  origin: Place;
  destination: Place;
  departure_date: string;
  return_date: string | null;
  non_stop_flight: boolean;
  traveler_count: TravelerCount;
  route_type: ROUTE_TYPE;
  cabin_class: CABIN_CLASS;
  currency_code?: string;
  legs?: MulticityLeg[];
  from_chip?: boolean;
  poll_id: string | null;
  full_result: boolean;
};

export type ReturnFlightPairStop = {
  label: string;
  value: number[];
};

export type FlightFilters = {
  airlines: FlightAirlneBaseFilter[];
  stops: FlightBaseFilter[];
  rtStops: ReturnFlightPairStop[];
  priceRange: Array<number>;
  departureTime: Array<any>;
};

export type SelectedFlightSearchQueryFilters = {
  airlines: Array<string>;
  stops: Array<string>;
  rtStops: Array<Array<number>>;
  priceRange: Array<number>;
  departureTime: Array<string>;
  maxPriceRangeSlider?: number;
};

export type FlightAirlneBaseFilter = {
  value: string;
  label: string;
  labelRt?: string;
};

export type FlightBaseFilter = {
  value: string;
  label: string;
};

export type returnFlightPairs = {
  price_difference?: number;
  currency: string;
  source: string;
  destination: string;
  name: string;
  logo: string;
  price: number;
  base_fare: number;
  gross_fare: number;
  tax: number;
  ag_gross_fare?: number;
  pre_booking_token: string;
  next_booking: string;
  rbdFirst: string;
  rbdSecond: string;
  goingFlightNo: string;
  comingFlightNo: string;
  goingBagage: BaggageInfo;
  comingBagage: BaggageInfo;
  firstLegArrivalTime: string;
  firstLegDepartureTime: string;
  secondLegArrivalTime: string;
  secondLegDepartureTime: string;
  firstLegDuration: string;
  secondLegDuration: string;
  firstLegAirport: string;
  secondLegAirport: string;
  FO_1: FareOption;
  FO_2: FareOption;
  departure_date: Date;
  arrival_date: Date;
  code: string;
  provider: string;
};

export type FlightOption = {
  provider: string;
  name: string;
  duration: number;
  departure_time: string;
  arrival_time: string;
  departure_arrival_day_difference: number;
  airline: Airline;
  flight_numbers: string[];
  cheapest_fare_option_price: ExtendedPrice;
  fare_options: FareOption[];
  updated_fare_options?: FareOption[];
  selected?: boolean;
  expiry?: number;
  is_ndc?: boolean;
  is_preferred_airline?: boolean;
  traveler_count?: TravelerCount;
};

export enum SEAT_STATUS {
  SEATS_AVAILABLE = "SEATS_AVAILABLE",
  WAITLISTED = "WAITLISTED",
}

export enum VIOLATION_ACTION {
  WARNING = "WARNING",
  BLOCK = "BLOCK",
  HIDE = "HIDE",
  APPROVAL_REQUIRED = "APPROVAL_REQUIRED",
  NOTIFY = "NOTIFY",
}

export type FareOption = {
  pre_booking_token: string;
  rbd: string;
  baggage_info?: BaggageInfo;
  price: Price;
  is_refundable: boolean;
  fare_option_seat_status: SEAT_STATUS;
  cancellation_policy_items: string[] | null;
  modification_policy_items: string[] | null;
  baggage_policy_items: string[] | null;
  has_meal: boolean;
  segments: Segment[];
  next_fare_option_ids: string[];
  discount_data: DealDiscount;
  out_of_policy: boolean;
  violation_action: VIOLATION_ACTION;
};

export interface Options {
  flight_option?: FlightOption;
  fare_option: FareOption;
  updated_fare_option?: FareOption;
  expiry?: number;
  traveler_count?: TravelerCount;
}

export interface ReturnFlightPair {
  departure: Options;
  arrival: Options;
}

export interface SABRE_AIRLINE {
  pcc: string | null;
  airline_code: string;
}

export interface GALILEO_AIRLINE {
  pcc: string | null;
  airline_code: string;
}

export interface FLIGHT_PROVIDER {
  name: string;
  value: string;
  sales_channel_name: string;
  is_ticket_issuance_enabled: boolean;
  is_active: boolean;
}

export interface ACTIVE_PROVIDERS_AND_AIRLINES_API_RESPONSE {
  active_sabre_airlines: SABRE_AIRLINE[];
  active_galileo_airlines: GALILEO_AIRLINE[];
  active_flight_providers: FLIGHT_PROVIDER[];
}

export interface IMultiCityAirports {
  origin: Place[];
  destination: Place[];
}

export type BookingActions = {
  is_ticket_issuance_enabled: boolean;
  is_refund_enabled: boolean;
  is_ticket_void_enabled: boolean;
  is_ticket_cancel_enabled: boolean;
  is_airline_ticket_enabled: boolean;
  is_download_ticket: boolean;
};

export interface PromoDeal {
  code: string;
  discount_offer: string;
  discount_type: string;
  status: string;
  discount_on_gross_fare: boolean;
  expiry_date: string;
  discount_amount: ExtendedPrice;
}

export interface BookingPromoDealDetails {
  total_discount_amount: ExtendedPrice | null;
  promo_deal_data: PromoDeal[];
}
